import { useEffect, useState } from "react";
import { Star, Trash2, Layers } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, fileUrl } from "../lib/api";
import { useI18n } from "../lib/i18n";

export default function Variations() {
  const { locale } = useI18n();
  const fr = locale === "fr";
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api.get("/media/batches").then(({ data }) => setBatches(data.batches)).catch(() => {})
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const act = async (assetId, patch, msg) => {
    try {
      await api.patch(`/assets/${assetId}`, patch);
      toast.success(msg);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="variations-page">
      <p className="overline">{fr ? "Comparateur" : "Comparison"}</p>
      <h1 className="mt-3 font-display text-3xl font-semibold sm:text-4xl">
        {fr ? "Vos variantes, côte à côte" : "Your variations, side by side"}
      </h1>
      <p className="mt-4 max-w-[620px] text-sm text-[#9194a6]">
        {fr ? "Chaque lot de 2, 4 ou 8 variantes d'un même plan est regroupé ici. Gardez la meilleure en favori, supprimez le reste."
            : "Every batch of 2, 4 or 8 variations of the same shot is grouped here. Favourite the best one, delete the rest."}
      </p>

      {!loading && batches.length === 0 && (
        <div className="mt-12 rounded-2xl border border-[#212230] bg-[#0b0b11] p-10 text-center" data-testid="variations-empty">
          <Layers className="mx-auto h-6 w-6 text-[#8288a0]" />
          <p className="mt-5 text-[13.5px] text-[#9194a6]">
            {fr ? "Aucun lot pour l'instant. Dans Créer, choisissez 2, 4 ou 8 variantes avant de générer."
                : "No batch yet. In Create, pick 2, 4 or 8 variations before generating."}
          </p>
        </div>
      )}

      <div className="mt-12 space-y-12">
        {batches.map((b) => (
          <div key={b.batch_id} data-testid={`batch-${b.batch_id}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <p className="max-w-[760px] text-[13.5px] text-[#c0c3d0]">{b.prompt || "—"}</p>
              <p className="mono shrink-0 text-[10.5px] text-[#8288a0]">
                {b.completed}/{b.count} {fr ? "terminées" : "done"} · {b.credits} cr
              </p>
            </div>
            <div className={`mt-5 grid gap-4 ${b.count > 2 ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-2"}`}>
              {b.generations.map((g, i) => (
                <div
                  key={g.id}
                  data-testid={`variation-${g.id}`}
                  className="overflow-hidden rounded-xl border border-[#212230] bg-[#0b0b11]"
                >
                  <div className="relative aspect-video bg-[#101118]">
                    {g.asset_id ? (
                      <video
                        src={fileUrl(g.asset_id)}
                        controls
                        preload="metadata"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center">
                        <p className="mono text-[10.5px] uppercase tracking-[0.14em] text-[#8288a0]">
                          {g.status}
                        </p>
                      </div>
                    )}
                    <span className="mono absolute left-3 top-3 rounded-full bg-black/70 px-2 py-0.5 text-[10px] text-white">
                      V{i + 1}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 p-3">
                    <p className="mono text-[10px] text-[#8288a0]">{g.model_id}</p>
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => act(g.asset_id, { favorite: true }, fr ? "Variante gardée" : "Variation kept")}
                        disabled={!g.asset_id}
                        data-testid={`variation-keep-${g.id}`}
                        className="btn-ghost rounded-full p-2 disabled:opacity-40"
                      >
                        <Star className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => act(g.asset_id, { is_deleted: true }, fr ? "Variante supprimée" : "Variation removed")}
                        disabled={!g.asset_id}
                        data-testid={`variation-delete-${g.id}`}
                        className="btn-ghost rounded-full p-2 disabled:opacity-40"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
